

import React, { useRef, useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';
import { Media, Card, Button, Label, Container, ListGroup, ListGroupItem, CardGroup, CardBody, CardHeader, Input, InputGroup, InputGroupAddon, UncontrolledCollapse, CardFooter, Collapse, Modal, ModalBody, ModalFooter, ModalHeader, FormGroup, CustomInput, ButtonGroup } from 'reactstrap';
import { Badge } from 'reactour';
import styled from 'styled-components';
import { useForm } from 'react-hook-form';

import {
    useSign,
    CONTROL_CHAT,
    useSignState
} from 'context/signview';
import { fetcher } from 'api/client';
import { getConversations, postConversation, getPrivate, createGroupConversation } from 'api/actions/contracts';
import { parseDate } from 'utils';
import { toast } from 'components/Shared/Toast';


const ChatContainer = styled.div`
position: fixed;
bottom: 0px;
right: 40px;
z-index: 1000;
width: 400px;
`

const ChatBodyContainer = styled.div`
position: fixed;
bottom: 0px;
right: 500px;
z-index: 1000;
width: 400px;
`

function ChatRoom({ room, innerState, setInnerState }) {

    const { t } = useTranslation();

    const { name, last, participants } = room;

    const handleActiveChat = () => {
        setInnerState({ ...innerState, room: room._id, name, email: null })
    }

    return (
        <ListGroupItem action active={innerState.room === room._id} onClick={handleActiveChat} className="pointer rounded-lg mb-3 shadow-sm">
            <Media>
                <Media className="mr-2">
                    <img src="/images/buho-avatar.png" height="50px" alt="" />
                </Media>
                <Media body className="text-truncate font-weight-bold">
                    <small><b>{t('chat.private')}</b>: {name}{' - '}{parseDate(last?.when || new Date().toISOString(), 'Pp')}</small>
                    {/* <p className="m-0 ">{last?.message}</p> */}
                    <ul>
                        {participants.map((p, index) => (<li key={`p${index}`}><small>{p}</small></li>))}
                    </ul>
                </Media>
            </Media>
        </ListGroupItem>
    )
}

function ChatParticipant({ participant = {}, innerState, currentEmail, setInnerState, conversation = [] }) {

    const { name, email } = participant;

    const handleActiveChat = () => {
        setInnerState({ ...innerState, email, name, room: null })
    }

    if (participant.email === currentEmail) return null;

    const last = conversation[conversation.length - 1]

    return (
        <ListGroupItem action active={innerState.email === participant.email} onClick={handleActiveChat} className="pointer rounded-lg mb-3 shadow-sm">
            <Media>
                <Media className="mr-2">
                    <img src="/images/buho-avatar.png" height="50px" alt="" />
                </Media>
                <Media body className="text-truncate font-weight-bold">
                    <small>{name}({email}){' - '}{last?.when && parseDate(last.when, 'Pp')}</small>
                    <p className="m-0 ">{last?.message}</p>
                </Media>
            </Media>
        </ListGroupItem>
    )
}

function CreateGroup({ innerState, toggle }) {

    const { t } = useTranslation();

    const [groupParticipants, setGroupParticipants] = useState([]);

    const groupName = useRef();

    const { contract, contractId, jwt, profile } = useSignState();

    const { mutate } = useMutation((payload) => fetcher(createGroupConversation(contractId, payload), jwt), {
        onSuccess: () => {
            toggle()
        }
    })

    const handleSetGroup = (e) => {
        if (e.target.checked) {
            const set = new Set(groupParticipants)
            set.add(e.target.value)
            setGroupParticipants([...set])
        } else {
            setGroupParticipants([...groupParticipants.filter(p => p !== e.target.value)])
        }
    }

    const handleCreate = () => {
        const roomName = groupName.current.value;
        mutate({ participants: groupParticipants, roomName })
    }

    return (
        <Modal isOpen={innerState.createGroup} centered>
            <ModalHeader toggle={toggle}>
                {t('chat.new_group')}
            </ModalHeader>
            <ModalBody>
                <FormGroup>
                    <Label>{t('chat.select_participants')}</Label>
                    <Label><b>{t('chat.group_name')}</b></Label>
                    <Input type="text" innerRef={groupName}></Input>
                </FormGroup>
                <FormGroup>
                    <Label><b>{t('chat.participants')}</b></Label>
                    {
                        contract?.participants?.filter(p => p.email !== profile.email).length ?
                            contract?.participants?.filter(p => p.email !== profile.email).map((participant, index) => (
                                <FormGroup className="pl-2 m-1" key={`pgroup-${index}`}>
                                    <input
                                        onChange={handleSetGroup}
                                        checked={groupParticipants.includes(participant.email)}
                                        type="checkbox"
                                        value={participant.email}
                                        name={participant._id}
                                        id={participant._id}
                                    />
                                    {' '}
                                    <Label>{participant.email}</Label>
                                </FormGroup>
                            )) :
                            <FormGroup>{t('chat.empty_participants')}</FormGroup>
                    }
                </FormGroup>
            </ModalBody>
            <ModalFooter>
                <ButtonGroup className="d-flex flex-grow-1">
                    <Button color="light" onClick={toggle} >{t('chat.close')}</Button>
                    <Button color="primary" onClick={handleCreate} disabled={!groupParticipants.length}>{t('chat.create')} </Button>
                </ButtonGroup>
            </ModalFooter>
        </Modal>
    )
}

function ChatGroupParticipant({ innerState, setInnerState }) {

    const { t } = useTranslation();

    const toggle = () => {
        setInnerState({ ...innerState, createGroup: !innerState.createGroup })
    }

    return (
        <ListGroupItem action className="pointer rounded-lg shadow-sm" onClick={toggle}>
            <CreateGroup toggle={toggle} innerState={innerState}></CreateGroup>
            <Media>
                {/* <Media className="mr-2">
                    <img src="/images/buho-avatar.png" height="50px" alt="" />
                </Media> */}
                <Media body className="text-center">
                    <span className="text-muted font-weight-bold"><i className="fas fa-plus "></i>{' '}{t('chat.new_group')}</span>
                </Media>
            </Media>
        </ListGroupItem>
    )
}

function Incoming({ conversation = {} }) {

    const {
        from,
        message,
        to,
        when
    } = conversation;

    return (
        <ListGroupItem action>
            <Media>
                <Media className="mr-2">
                    <img src="/images/buho-avatar.png" alt="avatar" height="50px" />
                </Media>
                <Media body className="w-100 text-break">
                    <div className="rounded-lg py-2 px-4 bg-secondary text-white">
                        <span className="">{message}</span>
                    </div>
                    <small className="text-muted"> {parseDate(when, 'Pp')}</small>
                </Media>
            </Media>
        </ListGroupItem>
    )
}

function Outgoing({ conversation = {} }) {

    const {
        from,
        message,
        to,
        when
    } = conversation;

    return (
        <ListGroupItem action>
            <Media>
                <Media body className="text-break w-100 float-right text-right">
                    <div className="rounded-lg py-2 px-4 bg-light">
                        <span className="">{message}</span>
                    </div>
                    <small className="text-muted"> {parseDate(when, 'Pp')}</small>
                </Media>
            </Media>
        </ListGroupItem>
    )
}

function Header({ text, open, isOpen, number, close }) {


    const handleOpen = () => {
        open(!isOpen)
    }

    return (
        <div className="d-flex py-2 px-3">
            {/* {number && <Badge accentColor="#ff385c">{number}</Badge>} */}
            <div className="ml-3">
                <img src="/images/owl-logo.svg" width="30px" className="profile-image mr-3" alt="owl"></img>
                <span className="text-center font-weight-bold">{text}</span>
            </div>
            <div className="ml-auto">
                <Button id="toggler" size="sm" color="default" onClick={handleOpen}>
                    <i className="fas fa-chevron-down"></i>
                </Button>
                <Button id="close" size="sm" color="default" onClick={close}>
                    <i className="fas fa-times"></i>
                </Button>
            </div>
        </div>
    )
}


function ChatBody({ innerState, contractId, currentEmail, setInnerState }) {

    const { email, name } = innerState;

    const { t } = useTranslation();

    const { jwt } = useSignState();

    const queryClient = useQueryClient();

    const messageRef = useRef();

    const messagesEndRef = useRef(null);

    const { mutate: sendMessageMutation } = useMutation((payload) => fetcher(postConversation(payload), jwt), {
        onSuccess: (data, vars) => {
            queryClient.setQueryData(`conversation-${email}`, [...conversations, { ...vars, from: currentEmail }]);
            scrollToBottom()
        }
    })

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
        messageRef.current?.focus();
    }

    const { data: conversations = [] } = useQuery(`conversation-${email}`, () => fetcher(getPrivate({ contractId, email }), jwt), { enabled: !!email })

    useEffect(() => {
        scrollToBottom();
    }, [conversations]);

    const handleSendMessage = () => {
        const message = messageRef.current.value;
        if (message) {
            messageRef.current.value = '';
            sendMessageMutation({
                contractId,
                message,
                name: name,
                to: email,
                when: new Date().toISOString()
            })
        }
    }

    const [collapse, setCollapse] = useState(true);

    const handleClose = () => {
        setInnerState({ ...innerState, email: null })
    }

    if (!email) return null;

    return (
        <Card>
            <CardHeader className="bg-white p-0">
                <Header text={email} open={setCollapse} isOpen={collapse} close={handleClose}></Header>
            </CardHeader>
            <Collapse isOpen={collapse}>
                <CardBody style={{ height: '500px', overflowX: 'auto' }}>
                    {conversations.filter(c => c.to === currentEmail).map((c, index) => <Incoming conversation={c} key={`conversation-in-${index}`} />)}
                    {conversations.filter(c => c.from === currentEmail).map((c, index) => <Outgoing conversation={c} key={`conversation-out-${index}`} />)}
                    <div ref={messagesEndRef}></div>
                </CardBody>
                <CardFooter className="m-0 p-0">
                    <ListGroupItem action>
                        <InputGroup>
                            <Input type="text" placeholder={t('sign.chat.send_message')} innerRef={messageRef} />
                            <InputGroupAddon addonType="">
                                <Button type="button" onClick={handleSendMessage}>
                                    <i className="fas fa-paper-plane"></i>
                                </Button>
                            </InputGroupAddon>
                        </InputGroup>
                    </ListGroupItem>
                </CardFooter>
            </Collapse>
        </Card>
    )
}

export default function Chat() {

    const { t } = useTranslation();

    const [{ chatOpen, contractId, contract, profile, jwt }, dispatch] = useSign();

    const participants = contract.participants || [];

    const [innerState, setInnerState] = useState({ email: null, minimized: false })

    const { data: { rooms = [], privates = [] } = {} } = useQuery('client-conversations', () => fetcher(getConversations(contractId), jwt), { enabled: chatOpen })

    const handleCloseChat = () => {
        dispatch({ type: CONTROL_CHAT, open: false });
    }

    const handleMinimize = () => {
        setInnerState({ ...innerState, minimized: !innerState.minimized })
    }

    if (!chatOpen) {
        return null;
    }

    return (
        <>
            <ChatBodyContainer className="shadow-sm">
                <ChatBody
                    setInnerState={setInnerState}
                    currentEmail={profile.email}
                    contractId={contractId}
                    innerState={innerState}
                    privates={privates}
                />
            </ChatBodyContainer>
            <ChatContainer className="shadow-sm">
                <Card>
                    <CardHeader className="bg-white p-0">
                        <Header text={t('chat.conversations')} open={handleMinimize} isOpen={innerState.minimized} number={1} close={handleCloseChat}></Header>
                    </CardHeader>
                    <Collapse isOpen={innerState.minimized}>
                        <CardBody style={{ height: '500px', overflowX: 'auto' }}>
                            <ListGroup>
                                {participants.map(((participant, index) =>
                                    <ChatParticipant
                                        key={`chat${index}`}
                                        contractId={contractId}
                                        innerState={innerState}
                                        participant={participant}
                                        setInnerState={setInnerState}
                                        currentEmail={profile.email}
                                        conversation={privates[participant.email]}
                                    />
                                ))}
                                {rooms.map((room) => (
                                    <ChatRoom
                                        innerState={innerState}
                                        setInnerState={setInnerState}
                                        room={room}
                                        key={room._id}
                                    />
                                ))}
                                <ChatGroupParticipant innerState={innerState} setInnerState={setInnerState} />
                            </ListGroup>
                        </CardBody>
                    </Collapse>
                </Card>
            </ChatContainer >
        </>
    )
}